import { useLocation, useSearchParams } from "react-router-dom";
import { useCallback, useMemo } from "react";

const useSearchParamsHandler = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const processAllParams = useCallback(() => {
    const paramsEntries = Array.from(searchParams.entries());
    const paramsObject = paramsEntries.reduce((acc, [key, value]) => {
      if (!acc[key]) {
        acc[key] = [];
      }
      if (!acc[key].includes(value)) {
        acc[key].push(value);
      }
      return acc;
    }, {});

    Object.keys(paramsObject).forEach((key) => {
      paramsObject[key] = paramsObject[key].join(",");
    });

    return paramsObject;
  }, [searchParams]);

  const addOrUpdateParam = useCallback(
    (key, value) => {
      const params = processAllParams();
      const existingValues = params[key] ? params[key].split(",") : [];

      if (!existingValues.includes(value)) {
        existingValues.push(value);
        const newParams = { ...params };

        if ((value + "").includes("-") && (key === "bw" || key === "rpb")) {
          newParams.page = "1";
          newParams.sort_by = "landed_cpr";
          newParams[key] = value;
        } else if (key === "page") {
          newParams[key] = value;
        } else {
          newParams.page = "1";
          newParams.sort_by = "landed_cpr";
          newParams[key] = existingValues.join(",");
        }

        setSearchParams(newParams, { replace: true });
      }
    },
    [processAllParams, setSearchParams],
  );

  const deleteParam = useCallback(
    (key, value) => {
      const params = processAllParams();
      const existingValues = params[key] ? params[key].split(",") : [];
      const filteredValues = existingValues.filter(
        (v) => v + "" !== value + "",
      );
      const newParams = { ...params };

      if (filteredValues.length === 0) {
        delete newParams[key];
        if (key !== "page") {
          newParams.page = "1";
        }
      } else {
        if (key === "page") {
          newParams[key] = filteredValues.join(",");
        } else {
          newParams.page = "1";
          newParams[key] = filteredValues.join(",");
        }
      }

      setSearchParams(newParams, { replace: true });
    },
    [processAllParams, setSearchParams],
  );

  const clearParams = useCallback(() => {
    const keepOnlySelectedKeys = (obj) => {
      const selectedKeys = [
        "wc",
        "sort_by",
        "caliber",
        "page",
        "user-zip",
        "pgId",
      ];

      const newObj = {};
      const pathnameParts = location.pathname.split("/");
      const lastPart = pathnameParts[pathnameParts.length - 1];
      const pgIdMatch = lastPart.match(/(?:^|-)(\d+)$/);
      const pgId = pgIdMatch ? pgIdMatch[1] : null;

      if (pgId) {
        newObj.pgId = pgId;
      }

      Object.keys(obj).forEach((key) => {
        if (selectedKeys.includes(key) && key !== "pgId") {
          newObj[key] = obj[key];
        }
      });

      return { ...newObj, page: "1", sort_by: "landed_cpr" };
    };

    setSearchParams(keepOnlySelectedKeys(processAllParams()), { replace: true });
  }, [location.pathname, processAllParams, setSearchParams]);

  const params = useMemo(() => processAllParams(), [processAllParams]);

  return {
    params,
    addOrUpdateParam,
    deleteParam,
    clearParams,
  };
};

export default useSearchParamsHandler;
